import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const skeletonVariants = cva('animate-pulse rounded-md bg-muted', {
  variants: {
    variant: {
      text: 'h-4 w-full',
      circle: 'rounded-full',
      rectangle: '',
      'table-row': 'h-12 w-full',
    },
  },
  defaultVariants: {
    variant: 'text',
  },
});

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  width?: string | number;
  height?: string | number;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, width, height, style, ...props }, ref) => {
    const dimensionStyle: React.CSSProperties = {
      ...style,
      ...(width !== undefined ? { width } : {}),
      ...(height !== undefined ? { height } : {}),
    };

    // For circle variant, ensure equal dimensions
    if (variant === 'circle') {
      const size = width ?? height ?? 40;
      dimensionStyle.width = size;
      dimensionStyle.height = size;
    }

    return (
      <div
        ref={ref}
        className={cn(skeletonVariants({ variant }), className)}
        style={dimensionStyle}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

function SkeletonGroup({
  count = 3,
  className,
  children,
  ...props
}: {
  count?: number;
  className?: string;
  children?: (index: number) => React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) {
  return (
    <div className={cn('space-y-3', className)} {...props}>
      {Array.from({ length: count }, (_, i) =>
        children ? children(i) : <Skeleton key={i} variant="text" />
      )}
    </div>
  );
}

SkeletonGroup.displayName = 'SkeletonGroup';

export { Skeleton, SkeletonGroup, skeletonVariants };
